import { DiscoverCursor, ErrorResponse, isErrorResponse } from "@types"

import customFetch, { APIError } from "./helper"
import {
  DiscoverFarcasterResp,
  ProfilesEntry,
  TrendingFcResp,
  TrendingResp,
} from "@/types/farcaster"

const API = "/discover"

/**
 * Retrieves a list of "your posts" based on the user's subscriptions.
 * @param cursor The page number to retrieve.
 * @param tag The tag to filter by.
 * @returns The your posts.
 */
export async function getYourSubscriptions(
  userId: string,
  page?: number | undefined,
  pageSize?: number | undefined
): Promise<TrendingResp> {
  try {
    console.log("About to get your posts...", page)

    const params = new URLSearchParams()
    if (page) params.append("page", page.toString())
    if (pageSize) params.append("pageSize", pageSize.toString())

    const url = `${API}/subscriptions/posts/${userId}${
      params.toString() ? `?${params.toString()}` : ""
    }`
    console.log("Full URL for subscription posts:", url)

    // Let's cache the response for 10 minutes with a revalidate.
    // This way, we can get the latest posts every 10 minutes.
    const response = await customFetch<TrendingResp | ErrorResponse>(
      url,
      {
        next: { revalidate: 600 }, // (600 seconds) 10 minutes.
      },
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      throw new Error(response.msg)
    }

    return response
  } catch (error) {
    console.error("Exception occurred fetching your posts", error)

    if (error instanceof Error) {
      throw new Error(
        `An error occurred while fetching subscription posts: ${error.message}`,
        { cause: error }
      )
    } else {
      throw new Error(
        `An error occurred while fetching subscription posts: ${String(error)}`
      )
    }
  }
}

/**
 * Retrieves the trending posts, based on a manually curated list of high reputation writers.
 * @param cursor The page number to retrieve.
 * @param tag The tag to filter by.
 * @returns The trending posts.
 */
export async function getTrendingPosts(
  cursor?: DiscoverCursor | undefined,
  tag?: string,
  pageSize?: number | undefined
): Promise<TrendingResp> {
  try {
    console.log("About to get trending posts...", cursor, tag)

    const params = new URLSearchParams()
    if (cursor) params.append("cursor", JSON.stringify(cursor))
    if (tag) params.append("tag", tag)
    if (pageSize) params.append("pageSize", pageSize.toString())

    const url = `${API}/trending${
      params.toString() ? `?${params.toString()}` : ""
    }`

    // Let's cache the response for 10 minutes with a revalidate.
    // This way, we can get the latest trending posts every 10 minutes.
    const response = await customFetch<TrendingResp | ErrorResponse>(
      url,
      {
        next: { revalidate: 600 }, // (600 seconds) 10 minutes.
      },
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Exception occurred", response)
      throw new Error(response.msg)
    }

    return response
  } catch (error) {
    console.error("Exception occurred fetching trending posts", error)

    if (error instanceof Error) {
      throw new Error(
        `An error occurred while fetching trending posts: ${error.message}`,
        { cause: error }
      )
    } else {
      throw new Error(
        `An error occurred while fetching trending posts: ${String(error)}`
      )
    }
  }
}

/**
 * Retrieves the recent posts.
 * @param cursor The page number to retrieve.
 * @param tag The tag to filter by.
 * @returns The recent posts.
 */
export async function getRecentPosts(
  cursor?: DiscoverCursor | undefined,
  tag?: string
): Promise<TrendingResp> {
  try {
    console.log("About to get recent posts...", cursor, tag)

    const params = new URLSearchParams()
    if (cursor) params.append("cursor", JSON.stringify(cursor))
    if (tag) params.append("tag", tag)

    const url = `${API}/recent${
      params.toString() ? `?${params.toString()}` : ""
    }`

    // Let's cache the response for 10 minutes with a revalidate.
    // This way, we can get the latest recent posts every 10 minutes.
    const response = await customFetch<TrendingResp | ErrorResponse>(
      url,
      {
        next: { revalidate: 600 },
      },
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Exception occurred", response)
      throw new Error(response.msg)
    }

    return response
  } catch (error) {
    console.error("Exception occurred fetching recent posts", error)

    if (error instanceof Error) {
      throw new Error(
        `An error occurred while fetching recent posts: ${error.message}`,
        { cause: error }
      )
    } else {
      throw new Error(
        `An error occurred while fetching recent posts: ${String(error)}`
      )
    }
  }
}

/**
 * Retrieves the trending farcaster posts.
 * @param page The page number to retrieve.
 * @param tag The tag to filter by.
 * @returns The trending farcaster posts.
 */
export async function getTrendingFarcasterPosts(
  page: number,
  tag?: string,
  pageSize?: number | undefined
): Promise<TrendingResp> {
  try {
    console.log("About to get trending farcaster posts...", page, tag)

    const params = new URLSearchParams()
    if (page) params.append("page", JSON.stringify(page))
    if (tag) params.append("tag", tag)
    if (pageSize) params.append("pageSize", pageSize.toString())

    const url = `${API}/trending/farcaster${
      params.toString() ? `?${params.toString()}` : ""
    }`

    // Let's cache the response for 10 minutes with a revalidate.
    // This way, we can get the latest trending posts every 10 minutes.
    const response = await customFetch<TrendingResp | ErrorResponse>(
      url,
      {
        next: { revalidate: 600 }, // (600 seconds) 10 minutes.
      },
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Exception occurred", response)
      throw new Error(response.msg)
    }

    return response
  } catch (error) {
    // Log and re-throw. We can let the error.tsx page handle the error.
    console.error("Exception occurred", error)

    if (error instanceof Error) {
      throw new Error(
        `An error occurred while fetching trending FC posts: ${error.message}`,
        { cause: error }
      )
    } else {
      throw new Error(
        `An error occurred while fetching trending FC posts: ${String(error)}`
      )
    }
  }
}

/**
 * Retrieves posts from everyone you follow on Farcaster.
 * @param page The page number to retrieve.
 * @returns The trending farcaster posts.
 */
export async function getFarcasterFollowerPosts(
  page: number,
  wallet_address: string,
  pageSize?: number | undefined
): Promise<TrendingFcResp> {
  try {
    console.log(
      "About to get trending farcaster posts...",
      page,
      wallet_address
    )

    const params = new URLSearchParams()
    if (page) params.append("page", JSON.stringify(page))
    if (pageSize) params.append("pageSize", pageSize.toString())

    const url = `${API}/farcaster/posts/${wallet_address}${
      params.toString() ? `?${params.toString()}` : ""
    }`

    // Let's cache the response for 10 minutes with a revalidate.
    // This way, we can get the latest trending posts every 10 minutes.
    const response = await customFetch<TrendingFcResp | ErrorResponse>(
      url,
      {
        next: { revalidate: 600 }, // (600 seconds) 10 minutes.
      },
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Exception occurred", response)
      throw new Error(response.msg)
    }

    return response
  } catch (error) {
    // If we get a 404 on this call, it just means the user isn't on FC.
    if (error instanceof APIError && error.status === 404) return []
    // Log and re-throw. We can let the error.tsx page handle the error.
    console.error("Exception occurred", error)

    if (error instanceof Error) {
      throw new Error(
        `An error occurred while fetching FC Follower posts: ${error.message}`,
        { cause: error }
      )
    } else {
      throw new Error(
        `An error occurred while fetching FC follower: ${String(error)}`
      )
    }
  }
}

/**
 * Retrieves the Farcaster account associated with a wallet address.
 * @param wallet_address The wallet address to lookup
 * @returns The Farcaster profile entry or false if none exists
 */
export async function getFarcasterAccount(
  wallet_address: string
): Promise<ProfilesEntry | false> {
  try {
    // Let's cache the response for 1 week with a revalidate
    const response = await customFetch<ProfilesEntry | false>(
      `${API}/farcaster/${wallet_address}/fcAddress`,
      {
        next: { revalidate: 604800 }, // (604800 seconds) 1 week
      },
      {
        bypassCookies: true,
      }
    )

    return response
  } catch (error) {
    console.error("Error fetching Farcaster account:", error)
    return false
  }
}

/**
 * Retrieves Farcaster users that the current user follows on Farcaster
 * who also have Paragraph publications
 *
 * This API endpoint connects the user's Farcaster social graph with Paragraph
 * publications, allowing users to discover and subscribe to people they already
 * follow on Farcaster.
 *
 * @param wallet_address The wallet address of the current user
 * @returns An array of Farcaster users with their Paragraph publication details or an error response
 */
export async function getFarcasterFollowers(
  wallet_address: string
): Promise<DiscoverFarcasterResp | ErrorResponse> {
  try {
    const response = await customFetch<DiscoverFarcasterResp>(
      `${API}/farcaster/${wallet_address}/followers`
    )

    return response
  } catch (error) {
    console.error("Error fetching Farcaster followers:", error)
    if (error instanceof APIError) {
      return {
        msg:
          error.data?.msg ||
          "Failed to load Farcaster users. Please try again later.",
      }
    }
    return { msg: "Failed to load Farcaster users. Please try again later." }
  }
}
